import React, { useCallback, useEffect, useMemo } from "react";
import MainLayout from "../../../layouts/MainLayout";
import { usePermissions } from "../../../hooks/usePermissions";
import { useNavigate, useLocation } from "react-router-dom";
import { faEye, faUnlock } from "@fortawesome/free-solid-svg-icons";
import ConfirmationModal from "../../../components/modals/ConfirmationModal";
import useLoader from "../../../hooks/useLoader";
import useNotification from "../../../hooks/useNotification";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import ListHeader from "../../../components/ListHeader";
import useAction from "../../../hooks/useAction";
import FilterCollapse from "../../../components/FilterCollapse";
import useUrlFilters from "../../../hooks/useUrlFilters";
import useGenericFilter from "../../../hooks/filters/useGenericFilter";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import AutoCompleteFilter from "../../../components/AutoCompleteFilter";
import baseService from "../../../services/baseService";
import TableWithInactiveFilter from "../../../components/table/TableWithInactiveFilter";
import useFilterHandlers from "../../../hooks/useFilterHandlers";
import {
  EquipmentEnum,
  EquipmentEnumPtBRLabelById,
} from "../../../enums/equipment-enum";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";
import { equipmentsStatusColorMap } from "../../../utils/statusColorMap";
import { DisposeModal } from "./components/DisposeModal";

const EquipmentToBeDisposalPage = () => {
  const { canAccess } = usePermissions();

  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = React.useState(false);

  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      "organization.name": [],
      number: [],
      "productItem.product.type.name": [],
      status_id: [],
      name: [],
      waiting_for_disposal: { $eq: true },
      is_completed: true, // trazer por padrão equipamentos com cadastro completos
      deleted_at: false,
    },
    "equipments",
  );

  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters);

  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        "organization.name": [],
        number: [],
        "productItem.product.type.name": [],
        status_id: [],
        name: [],
        waiting_for_disposal: { $eq: true },
        is_completed: true, // trazer por padrão equipamentos com cadastro completos
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear,
    );

  const { get: baseServiceGet } = useBaseService(navigate);
  const { hideLoader, showLoader } = useLoader();
  const { showNotification } = useNotification();

  // --- Funções de Transformação de Dados ---
  const mapStatus = useCallback((statusData) => {
    return Object.fromEntries(
      statusData.map((status) => [status.id, status.name]),
    );
  }, []);

  const { data: statusData, isLoading: isLoadingStatus } = useQueryWithLoader({
    queryKey: ["status"],
    queryFn: async () => {
      const response = await baseServiceGet(entities.status.get);
      return response.result;
    },
    staleTime: Infinity,
  });

  const statusMap = useMemo(() => {
    if (!statusData) return {};
    return mapStatus(statusData.data);
  }, [statusData, mapStatus]);

  const {
    data: equipmentsData,
    isLoading: isLoadingEquipments,
    isFetching: isFetchingEquipments,
    isError: isErrorEquipments,
    error: equipmentsError,
  } = useQueryWithLoader({
    queryKey: ["equipments", filters, pagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filters);
      const params = {
        filters: filtersTransformed,
        page: pagination.page,
        perPage: pagination.perPage,
        sort: {
          column: "id",
          order: "desc",
        },
      };
      const response = await baseServiceGet(entities.equipments.get, params);
      return response.result;
    },
    keepPreviousData: true,
    refetchOnWindowFocus: true,
    select: (data) => {
      return {
        equipments: data.data?.map((equipment) => ({
          id: equipment.id,
          name: equipment.name,
          number: equipment.number,
          serial_number: equipment.serial_number,
          status: EquipmentEnumPtBRLabelById[equipment?.status_id] || "-",
          status_id: equipment.status_id,
          organization: equipment.organization?.name,
          isDeleted: !!equipment.deleted_at,
          created_at: equipment.created_at,
        })),
        totalPages: data.last_page,
      };
    },
    enabled: !!statusMap,
    placeholderData: (previousData) => previousData,
  });

  const { equipments = [], totalPages = 1 } = equipmentsData || {};

  const columns = [
    { key: "number", label: "Número" },
    { key: "name", label: "Descrição", width: "50%" },
    { key: "serial_number", label: "Número de Série" },
    { key: "status", label: "Status" },
    { key: "organization", label: "Organização" },
    { key: "created_at", label: "Criado Em", align: "center" },
  ];

  useEffect(() => {
    if (isLoadingEquipments || isLoadingStatus) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isLoadingEquipments, isLoadingStatus, showLoader, hideLoader]);

  useEffect(() => {
    if (isErrorEquipments) {
      console.error(
        "Erro ao carregar equipamentos (useQuery):",
        equipmentsError,
      );
      showNotification(
        "error",
        `Erro ao carregar equipamentos: ${
          equipmentsError.response?.data?.message || equipmentsError.message
        }`,
      );
    }
  }, [isErrorEquipments, equipmentsError, showNotification]);

  const {
    openModalConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
    setSelectedItem,
    action,
  } = useAction(navigate);

  // useEffect para mensagens de estado de localização (mantido)
  useEffect(() => {
    if (location.state?.message) {
      showNotification("error", location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const actions = [
    {
      id: "details",
      icon: faEye,
      title: "Ver Detalhes",
      buttonClass: "btn-info",
      permission: "Ver equipamentos",
      onClick: (equipment) =>
        navigate(`/aluguel/equipamentos/detalhes/${equipment.id}`),
      show: (equipment) =>
        !equipment.isDeleted && canAccess("Ver equipamentos"),
    },
    {
      id: "disposible",
      icon: faUnlock,
      title: "Disponibilizar Equipamento",
      buttonClass: "btn-secondary",
      permission: "Disponibilizar equipamentos",
      onClick: (equipment) => {
        setOpen(!open);
        setSelectedItem(equipment);
      },
      show: (equipment) =>
        !equipment.isDeleted &&
        equipment.status_id !== EquipmentEnum.AVAILABLE &&
        canAccess("Disponibilizar equipamentos"),
    },
  ];

  //! implementação de filtro
  const inputsfilters = [
    {
      label: "Equipamento",
      component: (
        <AutoCompleteFilter
          service={baseService}
          columnDataBase={["number", "name"]}
          extraFilters={{ is_completed: { $eq: 1 } }}
          model="equipment"
          value={localFilterValues.number}
          valueKey="number"
          onChange={(selected) => {
            const val = selected?.value || selected;
            // Enviando um objeto com operador, o transformer passará ele direto!
            handleFilterChange("number", val ? { $eq: val } : null);
          }}
          placeholder="Selecione ou digite o numero ou a descrição"
        />
      ),
    },
    {
      label: "Organização",
      component: (
        <AutoCompleteFilter
          model="organization"
          service={baseService}
          columnDataBase="name"
          value={localFilterValues["organization.name"]} // modelo e coluna
          onChange={
            (selected) => handleFilterChange("organization.name", selected) // modelo e coluna
          }
          placeholder="Selecione ou digite a organização"
          isMulti
        />
      ),
    },
    {
      label: "Tipo",
      component: (
        <AutoCompleteFilter
          service={baseService}
          model="type"
          columnDataBase="name"
          value={localFilterValues["productItem.product.type.name"]}
          onChange={(selected) =>
            handleFilterChange("productItem.product.type.name", selected)
          }
          placeholder="Selecione ou digite o tipo"
          isMulti
        />
      ),
    },
  ];

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Equipamentos aguardando baixa"
        showBackButton={true}
        backUrl="/dashboard"
      />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsfilters}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        <ListHeader
          title="Lista de Equipamentos"
          buttonText="Tombar Equipamento"
          buttonLink="/aluguel/equipamentos/criar"
          permission="Criar equipamentos"
          canAccess={canAccess}
        />

        <TableWithInactiveFilter
          data={equipments}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value;
            setPagination({ page: 1, perPage: newPerPage });
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          rowColorConfig={{
            colorMap: equipmentsStatusColorMap,
            propertyKey: "status_id", // ou o campo que contém o ID do status
          }}
          isLoading={isLoadingEquipments}
          isFetching={isFetchingEquipments}
        />

        <DisposeModal
          data={selectedItem}
          open={open}
          onClose={() => setOpen(false)}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem ? selectedItem.name : ""}
          text={action.text}
        />
      </div>
    </MainLayout>
  );
};

export default EquipmentToBeDisposalPage;
